// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * CSV export for the Analyze panel.
 *
 * The numbers on screen are a summary; the export is the evidence. It
 * carries the full 256-bin distribution alongside the summary
 * statistics, because a histogram is the one artefact here that cannot
 * be reconstructed from the tiles — and because someone checking a
 * claim about a field wants the distribution, not a mean.
 *
 * The header block records what the numbers are *of*: the dataset, the
 * region, the units, and the quantisation step. A CSV that says
 * "mean, 0.000123" and nothing else is unfalsifiable a week later.
 *
 * `buildCsvText` is pure so the format is testable without a DOM. The
 * serialisation rules match `src/ui/publisher/analytics-charts.ts`
 * (RFC-4180, CRLF, quote only when needed) rather than importing it —
 * that module is publisher-portal-scoped.
 */

import {
  LUMA_LEVELS,
  type LumaHistogram,
  type RegionStats,
  type TransectSample,
  type ZonalSample,
} from '../services/datasetStats'
import { lumaToValue, releaseOf } from '../types/color-scale'
import { sourceEncoding } from '../services/decoderRange'
import { logCodeRatio, scaleCodeStep } from '../services/valueFidelity'
import type { CompareStats, RegionDifference } from '../services/research/compare'
import { trailFile } from '../services/research/researchTrail'
import type { DisplayColorScale } from '../types/unit-scale'

type Cell = string | number | null | undefined

function cell(value: Cell): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function rows(list: readonly Cell[][]): string {
  return list.map((r) => r.map(cell).join(',')).join('\r\n')
}

export interface CsvContext {
  datasetTitle: string | null
  scopeLabel: string
  /**
   * AYNI: where the numbers came from and how to reopen the view — the
   * dataset id, frame time, view link, credit, sources and licences
   * (`src/services/research/citation.ts` `provenanceRows`). Placed
   * after each export's own header rows.
   */
  provenance?: readonly (readonly Cell[])[]
  /**
   * AYNI: a frame held as a baseline over the same texels (research tier,
   * `src/services/research/baseline.ts`): the statistics and the zonal
   * profile then carry a baseline and a difference column.
   */
  baseline?: CsvBaseline
}

/** AYNI: what a CSV needs of a held baseline. */
export interface CsvBaseline {
  frameLabel: string | null
  datasetTitle: string | null
  /** A held frame's statistics; a period has none (it is compared place by place: `anomaly`). */
  stats?: RegionStats
  zonal: readonly ZonalSample[] | null
  /** AYNI: a period's difference per latitude row, over the cells both have (instead of mean − baseline_mean). */
  zonalDifference?: readonly (number | null)[]
  /** AYNI: a period's climatology (statistics S2): which years, how many frames were read, and how it was made. */
  period?: {
    span: string
    framesRead: string
    method: string
    framesSkipped?: number
    grouping?: string
    yearsPerGroup?: string
    cellFactor?: number
    sigmaFlooredShare?: number | null
    sigmaDof?: string
  }
  /** AYNI: the frame against the climatology, place by place where both have data. */
  anomaly?: { meanChange: number; meanSigma: number | null; shareBeyond2: number | null; areaKm2: number; cells: number }
}

/** AYNI: the provenance rows, copied so a header list can spread them. */
function provenanceRows(ctx: CsvContext): Cell[][] {
  return (ctx.provenance ?? []).map((r) => [...r])
}

/** AYNI: which baseline the difference columns are against. */
function baselineRows(ctx: CsvContext): Cell[][] {
  if (!ctx.baseline) return []
  return [
    ['baseline_frame', ctx.baseline.frameLabel ?? ''],
    ['baseline_dataset', ctx.baseline.datasetTitle ?? ''],
    ...(ctx.baseline.period
      ? [
          ['baseline_period', ctx.baseline.period.span],
          ['baseline_frames_read', ctx.baseline.period.framesRead],
          ...(ctx.baseline.period.framesSkipped !== undefined ? [['baseline_frames_skipped', ctx.baseline.period.framesSkipped]] : []),
          ...(ctx.baseline.period.grouping ? [['baseline_grouping', ctx.baseline.period.grouping]] : []),
          ...(ctx.baseline.period.yearsPerGroup ? [['baseline_years_per_group', ctx.baseline.period.yearsPerGroup]] : []),
          ...(ctx.baseline.period.cellFactor !== undefined ? [['baseline_cell_factor', ctx.baseline.period.cellFactor]] : []),
          ...(ctx.baseline.period.sigmaFlooredShare !== undefined ? [['baseline_sigma_floored_share', ctx.baseline.period.sigmaFlooredShare]] : []),
          ...(ctx.baseline.period.sigmaDof ? [['baseline_sigma_dof', ctx.baseline.period.sigmaDof]] : []),
          ['baseline_method', ctx.baseline.period.method],
          ['difference', 'value minus the climatology mean of its group, over the cells with data in both'],
        ] as Cell[][]
      : [['difference', 'value minus baseline']]),
  ]
}

/** AYNI: a period: the frame against the climatology, place by place. */
function anomalyRows(ctx: CsvContext): Cell[][] {
  const a = ctx.baseline?.anomaly
  if (!a) return []
  return [
    [],
    ['climatology_comparison', 'value'],
    ['mean_change', a.meanChange],
    ['mean_sigma_change', a.meanSigma],
    ['share_beyond_2_sigma', a.shareBeyond2],
    ['area_compared_km2', a.areaKm2],
    ['cells_compared', a.cells],
  ]
}

/**
 * AYNI: the data range and one code's width (value error audit,
 * 2026-10-19). For a release the range is the publisher's `vmin`..`vmax`
 * (in the scale's units), not the scale's extrapolated code-0 and code-255
 * values, and the step is the publisher's: a fixed width on a linear
 * release, a fixed ratio on a log one, none for classes.
 */
function rangeRows(scale: DisplayColorScale): Cell[][] {
  const release = releaseOf(scale)
  if (!release) return [['value_min', scale.vmin], ['value_max', scale.vmax], ['quantisation_step', Math.abs(scale.vmax - scale.vmin) / 255]]
  if (release.kind === 'luma8-linear') {
    // The scale is linear in decoded codes: read the data range off it, so a restated unit carries through.
    const at = (code: number): number => scale.vmin + (code / 255) * (scale.vmax - scale.vmin)
    return [['value_min', at(release.dataMinCode)], ['value_max', at(release.dataMaxCode)], ['quantisation_step', scaleCodeStep(scale) ?? '']]
  }
  const src = sourceEncoding(release)
  const rows: Cell[][] = [['value_min', src.vmin], ['value_max', src.vmax]]
  if (release.kind === 'luma8-log') rows.push(['quantisation_step_relative', (logCodeRatio(release) ?? 1) - 1])
  else rows.push(['quantisation_step', 'classes'])
  return rows
}

/**
 * The unit rows every export shares.
 *
 * `source_units` appears only when the scale was restated for
 * readability — `units, µg m-3` over a dataset the publisher stored as
 * `kg m-3`. The file is the artefact somebody checks a claim against a
 * week later, quite possibly against the model output itself, and the
 * two are the same measurement only if the file says so.
 */
function unitRows(scale: DisplayColorScale): Cell[][] {
  const list: Cell[][] = [['units', scale.units ?? '']]
  if (scale.sourceUnits) list.push(['source_units', scale.sourceUnits])
  return list
}

/**
 * Serialise a summary plus its distribution.
 *
 * Values are written at full precision rather than the three
 * significant digits the panel displays: the rounding is a presentation
 * choice about what is *legible*, and re-imposing it here would destroy
 * information the file exists to carry. The quantisation step is in the
 * header so a reader can see how much of that precision is real.
 */
export function buildCsvText(
  stats: RegionStats,
  hist: LumaHistogram,
  scale: DisplayColorScale,
  ctx: CsvContext,
): string {
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['region', ctx.scopeLabel],
    ...unitRows(scale),
    ...rangeRows(scale),
    ...provenanceRows(ctx),
    ...baselineRows(ctx),
    [],
    ...statRows(stats, ctx.baseline?.stats),
    ...anomalyRows(ctx),
    ['texels_with_data', stats.count],
    ['texels_examined', stats.examined],
    ['coverage_fraction', stats.coverage],
    [],
    ['value', 'area_km2', 'texel_count'],
  ]

  const bins: Cell[][] = []
  for (let luma = 0; luma < LUMA_LEVELS; luma++) {
    // Absent-data codes carry no area by construction; emitting them
    // would put rows in the file that the statistics above excluded.
    if (hist.weights[luma] <= 0) continue
    bins.push([lumaToValue(luma, scale), hist.weights[luma], hist.counts[luma]])
  }

  return `${rows(head)}\r\n${rows(bins)}\r\n`
}

/** AYNI: the statistics table, with baseline and difference columns when one applies. */
function statRows(stats: RegionStats, base: RegionStats | undefined): Cell[][] {
  const keys: Array<[string, keyof RegionStats]> = [
    ['mean', 'mean'], ['median', 'median'], ['min', 'min'], ['max', 'max'],
    ['p10', 'p10'], ['p90', 'p90'], ['std_dev', 'stdDev'], ['area_km2', 'areaKm2'],
  ]
  if (!base) return [['statistic', 'value'], ...keys.map(([name, k]): Cell[] => [name, stats[k] as number])]
  return [
    ['statistic', 'value', 'baseline', 'difference'],
    ...keys.map(([name, k]): Cell[] => [name, stats[k] as number, base[k] as number, (stats[k] as number) - (base[k] as number)]),
  ]
}

/**
 * Serialise a transect: one row per sample, in order along the line.
 *
 * Absent samples are kept with an empty value rather than dropped. A
 * reader reconstructing the profile needs to know the line crossed a
 * hole there — removing the row would close the gap silently, which is
 * the same failure the chart takes care to avoid.
 *
 * `sample_spacing_km` is in the header because it is the resolution
 * claim: the samples are placed one per grid cell crossed, so a reader
 * can see that a bump narrower than that spacing is not resolved.
 */
export function buildTransectCsvText(
  samples: readonly TransectSample[],
  scale: DisplayColorScale,
  ctx: CsvContext,
): string {
  const total = samples.length > 0 ? samples[samples.length - 1].distanceKm : 0
  const withData = samples.reduce((n, s) => n + (s.value == null ? 0 : 1), 0)
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['region', ctx.scopeLabel],
    ...unitRows(scale),
    ...rangeRows(scale),
    ['length_km', total],
    ['samples', samples.length],
    ['samples_with_data', withData],
    ['sample_spacing_km', samples.length > 1 ? total / (samples.length - 1) : 0],
    ...provenanceRows(ctx),
    [],
    ['distance_km', 'lat', 'lon', 'value'],
  ]
  const body = samples.map((s): Cell[] => [s.distanceKm, s.lat, s.lon, s.value])
  return `${rows(head)}\r\n${rows(body)}\r\n`
}

/**
 * Serialise a zonal profile: one row per image row, north to south.
 *
 * Rows with no data are kept with an empty mean, for the same reason
 * absent transect samples are — a latitude band nothing was measured at
 * is a fact about the field, and dropping the row would let a reader
 * join two latitudes that are not neighbours.
 *
 * `texel_count` rides along per row because it is what makes a mean
 * interpretable: near a pole, or at the edge of a region, a row can be
 * a handful of texels wide, and a mean over four texels does not
 * deserve the same weight as one over four thousand. The chart cannot
 * show that; the file can.
 */
export function buildZonalCsvText(
  samples: readonly ZonalSample[],
  scale: DisplayColorScale,
  ctx: CsvContext,
): string {
  const withData = samples.reduce((n, s) => n + (s.mean == null ? 0 : 1), 0)
  const zonalBase = ctx.baseline?.zonal && ctx.baseline.zonal.length === samples.length ? ctx.baseline.zonal : null
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['region', ctx.scopeLabel],
    ...unitRows(scale),
    ...rangeRows(scale),
    ['rows', samples.length],
    ['rows_with_data', withData],
    ...provenanceRows(ctx),
    ...(zonalBase ? baselineRows(ctx) : []),
    [],
    zonalBase ? ['lat', 'mean', 'texel_count', 'baseline_mean', 'difference'] : ['lat', 'mean', 'texel_count'],
  ]
  // AYNI: a baseline profile pairs row by row (same texels, same rows).
  const body = samples.map((s, i): Cell[] => {
    if (!zonalBase) return [s.lat, s.mean, s.count]
    const b = zonalBase[i].mean
    const paired = ctx.baseline?.zonalDifference
    return [s.lat, s.mean, s.count, b, paired ? paired[i] ?? null : s.mean === null || b === null ? null : s.mean - b]
  })
  return `${rows(head)}\r\n${rows(body)}\r\n`
}

/** Trigger a browser download. No-op-safe outside a DOM. */
export function downloadCsv(filename: string, text: string): void {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return
  // AYNI: a research record, when one is recording, keeps the file's name and SHA-256 (slice 4).
  trailFile({ name: filename, body: text, kind: 'csv' })
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** AYNI: what a Compare CSV says (research tier, 2026-10-06). */
export interface CompareCsv {
  datasetA: string | null
  datasetB: string
  units: string | undefined
  unitsB: string | undefined
  /** How B was put in A's units: `factor * b + offset`. */
  conversion: { factor: number; offset: number }
  frameA: string | null
  frameB: string | null
  pairing: string
  cellDegrees: number
  resolution: number | null
  stats: CompareStats
  provenance?: readonly (readonly Cell[])[]
  /** AYNI: where B came from: "real-time overlay" or "globe 2". */
  source?: string
  /** AYNI: what the map shows: A − B, or the difference as a % of B. */
  mode?: 'difference' | 'percent'
  /** AYNI: % mode: the |B| under which no percentage is drawn. */
  percentFloor?: number | null
  /** AYNI: the region the statistics cover; null for the whole globe. */
  region?: string | null
  /** AYNI: the region read on each side, side by side. */
  regionRows?: readonly RegionDifference[] | null
  /** AYNI: B's own provenance (its dataset, credit, sources and frame). */
  provenanceB?: readonly (readonly Cell[])[]
}

/**
 * AYNI: one dataset minus another, as a file: what was compared with what,
 * how B was converted and paired, the common grid, then the statistics at
 * full precision, the region side by side, and the provenance of both.
 */
export function buildCompareCsvText(c: CompareCsv): string {
  const s = c.stats
  const percent = c.mode === 'percent'
  const pairingRule = c.pairing === 'shown'
    ? 'as shown: the frames each showed were compared; their dates were not matched'
    : 'same date: within half the coarser time step'
  return rows([
    ['comparison', 'A minus B'],
    ['map', percent ? 'A minus B as a percentage of B: (A - B) / |B| x 100' : 'A minus B'],
    ...(percent ? [['percent_floor', c.percentFloor ?? ''], ['percent_rule', 'no percentage where |B| is under the floor: max(two quantisation steps of B, 2 % of the 95th percentile of |B|)']] : []),
    ...(c.source ? [['source_b', c.source]] : []),
    ['dataset_a', c.datasetA ?? ''],
    ['dataset_b', c.datasetB],
    ['units', c.units ?? ''],
    ['units_b', c.unitsB ?? ''],
    ['b_in_units_of_a', `${c.conversion.factor} * b + ${c.conversion.offset}`],
    ['frame_a', c.frameA ?? ''],
    ['frame_b', c.frameB ?? ''],
    ['frames', c.pairing],
    ['pairing_rule', pairingRule],
    ['region', c.region ?? 'whole globe'],
    ['common_grid_degrees', c.cellDegrees],
    ['regridding', 'both averaged onto the grid of the coarser dataset; a cell counts where at least half of it has data'],
    ['resolution', c.resolution ?? 'uneven (log scale)'],
    ['weights', 'cell area'],
    ['method', 'bias, RMS difference, centred RMS difference and pattern correlation after Taylor (2001), J. Geophys. Res. 106(D7), 7183-7192'],
    ...(c.provenanceB ? [['provenance_a']] : []),
    ...(c.provenance ?? []).map((r) => [...r]),
    ...(c.provenanceB ? [['provenance_b'], ...c.provenanceB.map((r) => [...r])] : []),
    [],
    ['statistic', 'value'],
    ['bias_mean_a_minus_b', s.bias],
    ['rms_difference', s.rmsd],
    ['centred_rms_difference', s.centredRmsd],
    ['pattern_correlation', Number.isFinite(s.patternCorrelation) ? s.patternCorrelation : ''],
    ['mean_a', s.meanA],
    ['mean_b', s.meanB],
    ...(percent && Number.isFinite(s.relativeBias) ? [['bias_percent_of_mean_b', s.relativeBias]] : []),
    ['cells_with_both', s.cells],
    ['area_km2', s.areaKm2],
    ['share_of_cells_with_both', s.overlap],
    ...(c.regionRows?.length
      ? [
          [],
          ['region_statistic', 'a', 'b_in_units_of_a', 'a_minus_b', ...(percent ? ['percent_of_b'] : [])],
          ...c.regionRows.map((r) => [r.key, r.a, r.b, r.difference, ...(percent ? [r.percent ?? ''] : [])]),
          ['region_note', 'area-weighted; each side over its own texels with data at its own resolution'],
        ]
      : []),
  ]) + '\r\n'
}
